import {
  Button,
  makeStyles,
  Typography,
  TextField,
  LinearProgress,
  Box,
  Chip
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ScheduleIcon from "@material-ui/icons/Schedule";
import React, { useState, useEffect } from "react";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import useToggle from "../../../state/useToggle";
import { useToastError } from "../../../components/FormError";
import usePollBatchStatus from "../../../hooks/usePollBatchStatus";
import useChunkedCsvDownload from "../../../hooks/useChunkedCsvDownload";
import api from "../../../api";

const StatusIcon = ({ status }) => {
  switch (status) {
    case "done":
      return <CheckCircleIcon style={{ color: "green" }} />;
    case "failed":
    case "finished_with_errors":
      return <ErrorIcon style={{ color: "red" }} />;
    case "in_progress":
      return <HourglassEmptyIcon style={{ color: "orange" }} />;
    default:
      return <ScheduleIcon style={{ color: "gray" }} />;
  }
};

export default function PostYardiCharges({ partnerId }) {
  const classes = useStyles();
  const toggle = useToggle();
  const toastError = useToastError();
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [response, setResponse] = useState("");

  const {
    download,
    isDownloading,
    progress: downloadProgress,
    reset: resetDownload
  } = useChunkedCsvDownload();

  const {
    logs,
    isPolling,
    isComplete,
    error: pollError,
    summary,
    startPolling,
    reset: resetPolling
  } = usePollBatchStatus();

  useEffect(() => {
    if (toggle.on) {
      setFile(null);
      setResponse("");
      setIsSubmitting(false);
      resetPolling();
      resetDownload();
    }
  }, [toggle.on, resetPolling, resetDownload]);

  const handleDownload = (e) => {
    e.preventDefault();
    download({
      requestChunk: ({ offset, limit, skipHeaders }) =>
        api.getYardiChargesCsv(partnerId, {
          offset,
          limit,
          skip_headers: skipHeaders
        }),
      filename: `yardi_charges_${partnerId}_${Date.now()}.csv`
    });
  };

  const handleFileChange = (event) => {
    const [selectedFile] = event.target.files || [];

    if (!selectedFile || selectedFile.type !== "text/csv") {
      toastError(new Error("Please select a valid CSV file"));
      event.target.value = null;
      return;
    }

    setFile(selectedFile);
    setResponse("");
    resetPolling();
  };

  const handleSubmit = async () => {
    if (!file) {
      toastError(new Error("Please select a CSV file to upload"));
      return;
    }

    setIsSubmitting(true);
    setResponse("");
    resetPolling();

    try {
      const formData = new FormData();

      formData.append("csv", file);

      const result = await api.postYardiCharges(partnerId, formData);

      // Start polling if we got log IDs back
      const logIds = result.data?.pmsRequestLogIds || [];
      if (logIds.length > 0) {
        startPolling(logIds);
        const skippedMsg =
          result.data.skippedCharges > 0
            ? ` (${result.data.skippedCharges} skipped - already posted)`
            : "";
        setResponse(
          `${result.data.totalCharges} charges queued in ${result.data.batchCount} batches.${skippedMsg} Monitoring progress...`
        );
      } else if (
        result.data?.skippedCharges > 0 &&
        result.data?.totalCharges === 0
      ) {
        // All charges were already posted
        setResponse(
          `All ${result.data.skippedCharges} charges were already posted. Nothing to process.`
        );
      } else {
        // Fallback for old API response format or message-only response
        const message = result.data?.message;
        const messages = result.data?.messages || [];
        setResponse(message || messages.join("\n"));
      }

      setFile(null);
    } catch (error) {
      const serverMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.response?.data?.messages?.join("\n") ||
        error.message ||
        "An error occurred while processing the file";

      toastError(serverMessage);
      setResponse(serverMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const extractErrorMessage = (log) => {
    const response = log.response || {};
    return (
      response.exception ||
      response.error ||
      response.message ||
      (response.messages || []).find((m) => !m.includes("successfully")) ||
      null
    );
  };

  const extractSuccessMessage = (log) => {
    const response = log.response || {};
    return (
      (response.messages || []).find((m) => m.includes("successfully")) ||
      response.message ||
      "Success"
    );
  };

  const handleDownloadFailedCsv = async () => {
    try {
      // Extract charge IDs from failed batches in current session
      const failedLogs = logs.filter((l) => l.status === "failed");
      const chargeIds = failedLogs.flatMap(
        (log) => log.options?.jobArgs?.pmsChargeIds || []
      );

      const response = await api.getFailedPmsChargesCsv(partnerId, chargeIds);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `failed_charges_${partnerId}_${Date.now()}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toastError(error.message || "Failed to download CSV");
    }
  };

  return (
    <div className={classes.buttonGroup}>
      <Button
        color="secondary"
        variant="contained"
        className={classes.action}
        onClick={toggle.toggle}
      >
        Post Rent Reporting Charges to Yardi
      </Button>

      <Typography>
        Post rent reporting charges to resident ledgers in PMS
      </Typography>

      <ConfirmationDialog
        open={toggle.on}
        onCancel={toggle.turnOff}
        onConfirm={handleSubmit}
        title="Post Yardi Charges"
        cancelProps={{ children: "Close" }}
        confirmProps={{
          children: isSubmitting ? "Submitting..." : "Submit",
          disabled: isSubmitting || !file || isPolling
        }}
        maxWidth="md"
        fullWidth
      >
        <div className={classes.modalContent}>
          <div className={classes.modalInputs}>
            <Typography>
              Download the CSV partner template file{" "}
              <Button
                onClick={handleDownload}
                className={classes.downloadLink}
                disableRipple
                disableFocusRipple
                disabled={isDownloading}
              >
                {isDownloading ? "downloading…" : "here"}
              </Button>
              .
            </Typography>
            {isDownloading && downloadProgress && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={
                    (downloadProgress.loaded / downloadProgress.total) * 100
                  }
                />
                <Typography variant="caption" color="textSecondary">
                  Loaded {downloadProgress.loaded} of {downloadProgress.total}{" "}
                  batches…
                </Typography>
              </Box>
            )}

            <Typography>
              Review and modify the CSV file with the relevant information,
              submit the file below and confirm the charges were posted
              successfully.
            </Typography>

            <div>
              <input
                accept=".csv"
                className={classes.fileInput}
                id="csv-upload"
                type="file"
                onChange={(e) => {
                  handleFileChange(e);
                  e.target.value = "";
                }}
              />
              <label htmlFor="csv-upload">
                <Button
                  variant="outlined"
                  component="span"
                  disabled={isPolling}
                >
                  {file ? file.name : "Upload CSV"}
                </Button>
              </label>
            </div>

            {/* Batch Status Display */}
            {isPolling || logs.length > 0 ? (
              <Box className={classes.batchStatus}>
                <Typography variant="subtitle1" gutterBottom>
                  {isPolling ? "Processing batches..." : "Processing complete"}
                </Typography>

                <Box className={classes.chipContainer}>
                  <Chip
                    label={`${summary.done} done`}
                    size="small"
                    className={classes.chipDone}
                  />
                  <Chip
                    label={`${summary.failed + summary.withErrors} failed`}
                    size="small"
                    className={classes.chipFailed}
                  />
                  {summary.inProgress > 0 ? (
                    <Chip
                      label={`${summary.inProgress} in progress`}
                      size="small"
                      className={classes.chipProgress}
                    />
                  ) : null}
                  {summary.pending > 0 ? (
                    <Chip
                      label={`${summary.pending} pending`}
                      size="small"
                      variant="outlined"
                    />
                  ) : null}
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={
                    summary.total > 0
                      ? (summary.completed / summary.total) * 100
                      : 0
                  }
                  className={classes.progressBar}
                />

                <div className={classes.batchList}>
                  {logs.map((log, idx) => (
                    <div key={log.id} className={classes.batchItem}>
                      <span className={classes.batchIcon}>
                        <StatusIcon status={log.status} />
                      </span>
                      <span className={classes.batchText}>
                        <strong>Batch {idx + 1}:</strong> {log.status}
                        {log.status === "failed" ? (
                          <span className={classes.errorText}>
                            {" - "}
                            {extractErrorMessage(log)}
                          </span>
                        ) : null}
                        {log.status === "done" ? (
                          <span className={classes.successText}>
                            {" - "}
                            {extractSuccessMessage(log)}
                          </span>
                        ) : null}
                      </span>
                    </div>
                  ))}
                </div>

                {isComplete && summary.failed + summary.withErrors > 0 ? (
                  <Box className={classes.alertBox}>
                    <Typography color="error" gutterBottom>
                      {summary.failed + summary.withErrors} batch(es) failed.
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={handleDownloadFailedCsv}
                    >
                      Download Failed Charges CSV
                    </Button>
                  </Box>
                ) : null}

                {isComplete &&
                summary.failed === 0 &&
                summary.withErrors === 0 ? (
                  <Box className={classes.successBox}>
                    <Typography style={{ color: "green" }}>
                      All {summary.total} batches processed successfully!
                    </Typography>
                  </Box>
                ) : null}

                {pollError ? (
                  <Box className={classes.alertBox}>
                    <Typography color="error">{pollError}</Typography>
                  </Box>
                ) : null}
              </Box>
            ) : null}

            {/* Original output field - only show when not polling */}
            {!isPolling && logs.length === 0 ? (
              <div>
                <Typography variant="subtitle1">Output:</Typography>
                <TextField
                  multiline
                  fullWidth
                  minRows={6}
                  value={response}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    className: classes.responseText
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </ConfirmationDialog>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  action: {
    marginRight: theme.spacing(2),
    boxSizing: "content-box"
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  modalContent: {
    padding: theme.spacing(2)
  },
  modalInputs: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  responseText: {
    fontFamily: "monospace",
    backgroundColor: theme.palette.grey[50]
  },
  fileInput: {
    display: "none"
  },
  uploadButton: {
    minWidth: 200
  },
  downloadLink: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    padding: 0,
    minWidth: "auto",
    textTransform: "none",
    "&:hover": {
      color: theme.palette.primary.dark,
      backgroundColor: "transparent"
    }
  },
  batchStatus: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  chipContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  chipDone: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText
  },
  chipFailed: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText
  },
  chipProgress: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText
  },
  progressBar: {
    marginBottom: theme.spacing(2)
  },
  batchList: {
    maxHeight: 300,
    overflowY: "auto"
  },
  batchItem: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  batchIcon: {
    marginRight: theme.spacing(1),
    display: "flex",
    alignItems: "center"
  },
  batchText: {
    flex: 1,
    fontSize: "0.875rem"
  },
  errorText: {
    color: theme.palette.error.main
  },
  successText: {
    color: theme.palette.success.main
  },
  alertBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.error.light,
    borderRadius: theme.shape.borderRadius
  },
  successBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.success.light,
    borderRadius: theme.shape.borderRadius
  }
}));
